import { useEffect } from 'react'
import { useRouter } from '@tanstack/react-router'
import { registerSessionEndedHandler } from '../api/session'
import { useQueueViewStore } from '../features/queue/queueViewStore'

/**
 * Connects the api layer's session lifecycle to the router — docs/spec/06 R1.
 *
 * The api layer deliberately never imports the router (01 §2), so the reaction
 * is registered here instead. Bound to the router from CONTEXT rather than a
 * module-scope instance: an import-time binding drives whichever router the
 * module happened to create, which is not necessarily the one on screen.
 */
export function SessionBridge(): null {
  const router = useRouter()

  useEffect(() => {
    registerSessionEndedHandler(() => {
      // The queue view is session-scoped like the tokens (04 §3c). Cleared here
      // rather than on unmount: unmount does not run on every exit, and one
      // operator's filters must not greet the next person at a shared machine.
      useQueueViewStore.getState().clear()

      // Deliberately no `search`: the route guard attaches ?redirect= when it
      // fires, and the login screen decides whether to honour it based on WHY
      // the session ended. Racing the guard to set it here is what made this
      // non-deterministic.
      void router.navigate({ to: '/login', replace: true })
    })

    return () => registerSessionEndedHandler(null)
  }, [router])

  return null
}
